import type { ConfirmationOptions } from 'primevue/confirmationoptions'

import { useConfirm } from 'primevue/useconfirm'

/**
 * Composable for managing PrimeVue Confirmation Dialog
 * Provides a wrapper around PrimeVue's useConfirm with common configurations
 */
export const useConfirmationService = () => {
  const confirm = useConfirm()

  /**
   * Show a confirmation dialog with common configuration
   * @param options - Confirmation options
   */
  const showConfirmation = (options: ConfirmationOptions) => {
    const defaultOptions: Partial<ConfirmationOptions> = {
      header: 'Confirmation',
      rejectProps: {
        label: 'Cancel',
        severity: 'secondary',
        outlined: true
      },
      acceptProps: {
        label: 'Confirm'
      }
    }

    // Merge default options with provided options
    confirm.require({
      ...defaultOptions,
      blockScroll: true,
      ...options,
      // A dismissal — the X, Escape or a backdrop click — calls only `onHide`, never `reject`. Treat it
      // as a reject, so a caller that reverts or settles a promise there is not left half-done.
      onHide: options.onHide ?? options.reject
    })
  }

  /**
   * Show a confirmation dialog for saving an item
   * @param options - Partial confirmation options to override defaults
   */
  const confirmSave = (options: Partial<ConfirmationOptions> = {}) => {
    showConfirmation({
      message: 'Are you sure you want to save these changes?',
      header: 'Save Changes',
      acceptProps: {
        label: 'Save'
      },
      ...options
    })
  }

  /**
   * Show a confirmation dialog for deleting an item
   * @param options - Partial confirmation options to override defaults
   */
  const confirmDelete = (options: Partial<ConfirmationOptions> = {}) => {
    showConfirmation({
      message: 'Do you want to delete this record?',
      header: 'Danger Zone',
      acceptProps: {
        label: 'Delete',
        severity: 'danger'
      },
      ...options
    })
  }

  /**
   * Show a confirmation dialog for canceling an action
   * @param options - Partial confirmation options to override defaults
   */
  const confirmCancel = (options: Partial<ConfirmationOptions> = {}) => {
    showConfirmation({
      message: 'Are you sure you want to cancel? Any unsaved changes will be lost.',
      header: 'Cancel Action',
      acceptProps: {
        label: 'Yes, Cancel'
      },
      ...options
    })
  }

  /**
   * Show a confirmation dialog warning about duplicate SKU/barcode
   * @param options - Partial confirmation options to override defaults
   */
  const confirmDuplicateSku = (options: Partial<ConfirmationOptions> = {}) => {
    showConfirmation({
      message: 'This barcode is already registered to another product.',
      header: 'Duplicate SKU Warning',
      rejectProps: {
        label: 'Cancel',
        severity: 'secondary',
        outlined: true
      },
      acceptProps: {
        label: 'Create Anyway',
        severity: 'warn'
      },
      ...options
    })
  }

  /**
   * Show a confirmation dialog when SKU check is still in progress
   * Gives user choice to wait for check or proceed immediately
   * @param options - Partial confirmation options to override defaults
   */
  const confirmSkuCheckInProgress = (options: Partial<ConfirmationOptions> = {}) => {
    showConfirmation({
      message:
        "We're still checking if this barcode is already registered to an existing product.\n\nWould you like to wait for the check to complete, or proceed now?",
      header: 'SKU Check In Progress',
      rejectProps: {
        label: 'Wait for Check',
        severity: 'secondary',
        outlined: true
      },
      acceptProps: {
        label: 'Generate Anyway',
        severity: 'warn'
      },
      ...options
    })
  }

  return {
    showConfirmation,
    confirmSave,
    confirmDelete,
    confirmCancel,
    confirmDuplicateSku,
    confirmSkuCheckInProgress
  }
}
